import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { ObstructionDTO } from '../../models/ObstructionDTO.model'
import FlowfeedApiQueryService, {
  FlowfeedApiQueryServiceGetEvent,
} from '../FlowfeedApiQueryService/FlowfeedApiQueryService'
import { TrailService } from '../TrailService/TrailService'
import { formatToDateTypeProps, formatToNumberTypeProps } from '../../utils/formatting'
import { ObstructionReportSearchEvent } from '../../events/ObstructionReportSearchEvent'
import { ObstructionReportsRelations } from '../../models/ObstructionReportsRelations.model'
import { ObstructionReport } from '../../models/ObstructionReport.model'
import { Trail } from '../../models/Trail.model'
import { AppRegions } from '../../models/AppRegions.model'

export class ObstructionReportsService {
  /**
   * @description Search for a single obstruction by id
   *
   * @param id
   * @param relations
   *
   * @returns promise for a Obstruction
   */
  public async get({
    id,
    relations,
  }: {
    id?: number
    relations?: ObstructionReportsRelations[]
  }): Promise<ObstructionReport> {
    const params = {
      id,
      relations:
        Array.isArray(relations) && !relations.length
          ? 'false' // false is treated as an empty array by the API since an empty string would be treated as default relations normally
          : relations?.join(',') || undefined,
    }
    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: `obstruction-report/get`,
      params,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const obstructionRaw = await flowfeedApiQueryService.get<ObstructionDTO>(flowfeedApiQueryRequest)
      return ObstructionReportsService.formatObstructionReport(obstructionRaw)
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  public async search(event: ObstructionReportSearchEvent): Promise<PaginatedResponse<ObstructionReport>> {
    const params = {
      ...event,
      isExpired: event.isExpired,
      orderDirection: event.orderDirection?.toUpperCase(),
      relations:
        Array.isArray(event.relations) && !event.relations.length
          ? 'false' // false is treated as an empty array by the API since an empty string would be treated as default relations normally
          : event.relations?.join(',') || undefined,
    }

    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: 'obstruction-report',
      params,
    }

    const obstructions: {
      data: ObstructionReport[]
      total: number
    } = {
      data: [],
      total: 0,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const rawObstructions = await flowfeedApiQueryService.get<PaginatedResponse<ObstructionDTO>>(
        flowfeedApiQueryRequest,
      )
      // wrap to avoid errors if the request is cancelled
      if (rawObstructions?.data) {
        obstructions.data = rawObstructions.data.map((obstruction) =>
          ObstructionReportsService.formatObstructionReport(obstruction),
        )
        obstructions.total = rawObstructions.total
      }
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }

    return obstructions
  }

  public async delete(id: number): Promise<void> {
    const flowfeedApiQueryRequest = {
      endpoint: `obstruction-report/${id}`,
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.delete(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  /**
   * @description Push an active report's expiry out by a number of days so the hourly cleanup doesn't archive it
   *
   * @param id
   * @param days whole number of days to add to the current expiry
   *
   * @returns promise for the updated obstruction report
   */
  public async extend(id: number, days: number): Promise<ObstructionReport> {
    return this.patchExpiry(id, 'extend', days)
  }

  /**
   * @description Bring an archived (expired or closed) report back onto the public trail pages
   *
   * @param id
   * @param days whole number of days it stays active, counted from now
   *
   * @returns promise for the reactivated obstruction report
   */
  public async reactivate(id: number, days: number): Promise<ObstructionReport> {
    return this.patchExpiry(id, 'reactivate', days)
  }

  private async patchExpiry(id: number, action: 'extend' | 'reactivate', days: number): Promise<ObstructionReport> {
    const flowfeedApiQueryRequest = {
      endpoint: `obstruction-report/${id}/${action}`,
      data: { days },
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const obstructionRaw = await flowfeedApiQueryService.patch<ObstructionDTO>(flowfeedApiQueryRequest)
      return ObstructionReportsService.formatObstructionReport(obstructionRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async import(
    obstructions: (Partial<ObstructionReport> & {
      trailId?: number
      systemId?: number
    })[],
    options?: {
      matchingField?: string | undefined
      deleteUnmatchedExisting?: boolean
    },
  ): Promise<void> {
    const data = obstructions.map((obstruction) => {
      const numberTypeProps = ['id', 'trailId', 'systemId']
      const dateTypeProps = ['start', 'end']
      const formattedObstruction = formatToDateTypeProps(
        formatToNumberTypeProps(obstruction, numberTypeProps),
        dateTypeProps,
      )
      return {
        ...formattedObstruction,
      }
    })
    const flowfeedApiQueryRequest = {
      endpoint: `obstruction-report/sync`,
      data: {
        data,
        matchingProp: options?.matchingField,
        deleteMissing: options?.deleteUnmatchedExisting,
      },
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.post(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public static formatObstructionReport(obstructionReport: ObstructionDTO): ObstructionReport {
    return {
      id: obstructionReport.id,
      alertLevel: obstructionReport.alertLevel || '',
      trail: obstructionReport.trail ? TrailService.formatTrail(obstructionReport.trail) : ({} as Trail),
      obstructionType: obstructionReport.obstructionType
        ? {
            id: obstructionReport.obstructionType.id,
            name: obstructionReport.obstructionType.name,
            symbol: obstructionReport.obstructionType.symbol,
            active: obstructionReport.obstructionType.active,
            sortWeight: obstructionReport.obstructionType.sortWeight,
            createdAt: obstructionReport.obstructionType.createdAt,
          }
        : {
            id: undefined,
            name: '',
            symbol: '',
            active: false,
            sortWeight: 0,
            createdAt: undefined,
          },
      obstructionDescription: obstructionReport.obstructionDescription || '',
      statusDescription: obstructionReport.statusDescription || '',
      reporterEmail: obstructionReport.reporterEmail || '',
      region: {
        code: obstructionReport.region.code as AppRegions,
        name: obstructionReport.region.name || '',
      },
      imageUrl: obstructionReport.imageUrl || undefined,
      gpsLat: obstructionReport.gpsLat || '',
      gpsLong: obstructionReport.gpsLong || '',
      reportedAt: obstructionReport.reportedAt || '',
      expiryDate: obstructionReport.expiryDate || '',
      system: obstructionReport.system || '',
      archived: obstructionReport.archived || false,
    }
  }
}
